<template>
  <div class="step-regras">
    <div class="step-regras__hint">
      Regras de <strong>classificação</strong>: produtos que casam em
      <strong>TODAS</strong> as regras (AND) entram neste grupo. O modo de
      agrupamento (na próxima aba) decide como esses produtos serão
      subdivididos em matrizes.
    </div>

    <div v-if="!regras.length" class="step-regras__empty">
      <CIcon name="cil-ban" size="lg" class="empty-icon" />
      <p>Nenhuma regra cadastrada</p>
      <small>Adicione pelo menos 1 regra para vincular produtos.</small>
    </div>

    <div v-else class="step-regras__table-wrapper">
      <table class="step-regras__table">
        <thead>
          <tr>
            <th class="col-coluna">Atributo</th>
            <th class="col-valor">Valor</th>
            <th class="col-acoes"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(regra, indice) in regras" :key="`r-${indice}`">
            <td class="col-coluna">
              <multiselect
                :value="opcaoColuna(regra)"
                :options="colunasFiltradas(regra.colunaProduto)"
                :searchable="true"
                :show-labels="false"
                :allow-empty="false"
                label="label"
                track-by="coluna"
                placeholder="Selecione o atributo..."
                @select="onColunaChange(indice, $event)"
              />
            </td>
            <td class="col-valor">
              <DropdownInput
                v-if="regra.colunaProduto === 'id'"
                :value="regra.valor"
                label=""
                @input="onProdutoChange(indice, $event)"
              />
              <multiselect
                v-else-if="regra.colunaProduto && temValoresPredef(regra.colunaProduto)"
                :value="opcaoValor(regra)"
                :options="valoresPorColuna[regra.colunaProduto] || []"
                :searchable="true"
                :show-labels="false"
                :allow-empty="false"
                :loading="!!loadingValores[regra.colunaProduto]"
                label="label"
                track-by="id"
                :placeholder="loadingValores[regra.colunaProduto] ? 'Carregando...' : 'Selecione um valor...'"
                @select="onValorChange(indice, $event)"
              >
                <template #option="{ option }">
                  <span class="multiselect-option-id">{{ option.id }}</span>
                  <span class="multiselect-option-label">{{ option.label }}</span>
                </template>
                <template #singleLabel="{ option }">
                  <span class="multiselect-option-id">{{ option.id }}</span>
                  {{ option.label }}
                </template>
              </multiselect>
              <input
                v-else
                type="text"
                class="form-control"
                :placeholder="regra.colunaProduto ? 'Informe o valor exato' : 'Selecione a coluna primeiro'"
                :disabled="!regra.colunaProduto"
                v-model="regra.valor"
                maxlength="100"
              />
            </td>
            <td class="col-acoes">
              <CButton size="sm" color="danger" @click="remover(indice)" title="Remover">
                <fa-icon icon="trash-alt" />
              </CButton>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <CButton color="primary" variant="outline" size="sm" @click="adicionar" class="mt-2">
      <CIcon name="cil-plus" size="sm" class="mr-1" />
      Adicionar regra
    </CButton>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import DropdownInput from "@/views/Campanhas/components/CampanhasComerciais/components/Regra/DropdownInput.vue";

export default {
  name: "StepRegras",
  components: { Multiselect, DropdownInput },
  props: {
    form: { type: Object, required: true },
    colunasDisponiveis: { type: Array, default: () => [] },
  },
  data() {
    return {
      valoresPorColuna: {},
      loadingValores: {},
    };
  },
  computed: {
    regras() {
      if (!this.form.regras) {
        this.$set(this.form, "regras", []);
      }
      return this.form.regras;
    },
    colunasMap() {
      return this.colunasDisponiveis.reduce((acumulador, opcao) => {
        acumulador[opcao.coluna] = opcao;
        return acumulador;
      }, {});
    },
  },
  watch: {
    regras: {
      immediate: true,
      handler(novasRegras) {
        (novasRegras || []).forEach((regra) => {
          if (regra.colunaProduto && this.temValoresPredef(regra.colunaProduto)) {
            this.garantirValoresCarregados(regra.colunaProduto);
          }
        });
      },
    },
  },
  methods: {
    adicionar() {
      this.regras.push({ colunaProduto: null, valor: "" });
    },
    remover(indice) {
      this.regras.splice(indice, 1);
    },
    colunasFiltradas(colunaAtual) {
      const colunasUsadas = this.regras
        .map((regra) => regra.colunaProduto)
        .filter((coluna) => coluna && coluna !== colunaAtual);
      return this.colunasDisponiveis.filter(
        (opcao) => !colunasUsadas.includes(opcao.coluna)
      );
    },
    opcaoColuna(regra) {
      if (!regra.colunaProduto) return null;
      return this.colunasMap[regra.colunaProduto] || null;
    },
    opcaoValor(regra) {
      if (!regra.colunaProduto || !regra.valor) return null;
      const valoresDisponiveis = this.valoresPorColuna[regra.colunaProduto] || [];
      return (
        valoresDisponiveis.find((valor) => String(valor.id) === String(regra.valor)) ||
        null
      );
    },
    onColunaChange(indice, opcaoSelecionada) {
      const novaColuna = opcaoSelecionada ? opcaoSelecionada.coluna : null;
      this.$set(this.regras[indice], "colunaProduto", novaColuna);
      this.$set(this.regras[indice], "valor", "");
      if (novaColuna && this.temValoresPredef(novaColuna)) {
        this.garantirValoresCarregados(novaColuna);
      }
    },
    onValorChange(indice, opcaoSelecionada) {
      this.$set(
        this.regras[indice],
        "valor",
        opcaoSelecionada ? String(opcaoSelecionada.id) : ""
      );
    },
    onProdutoChange(indice, produtoId) {
      this.$set(this.regras[indice], "valor", produtoId ? String(produtoId) : "");
    },
    temValoresPredef(coluna) {
      const opcaoColuna = this.colunasMap[coluna];
      return !!(opcaoColuna && opcaoColuna.tem_valores_predef);
    },
    garantirValoresCarregados(coluna) {
      if (this.valoresPorColuna[coluna] || this.loadingValores[coluna]) return;
      this.$set(this.loadingValores, coluna, true);
      get(`/admin/grupo-grade/valores-coluna?coluna=${encodeURIComponent(coluna)}`)
        .then((json) => {
          const valores = json.data || json || [];
          this.$set(this.valoresPorColuna, coluna, valores);
        })
        .catch(() => {
          this.$set(this.valoresPorColuna, coluna, []);
        })
        .finally(() => {
          this.$set(this.loadingValores, coluna, false);
        });
    },
  },
};
</script>

<style scoped>
.step-regras {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.step-regras__hint {
  font-size: 13px;
  color: #475467;
  padding: 10px 12px;
  background: #f9fafb;
  border-left: 3px solid #3a3e81;
  border-radius: 4px;
}

.step-regras__empty {
  text-align: center;
  padding: 32px 20px;
  color: #98a2b3;
  background: #f9fafb;
  border: 1px dashed #dddce9;
  border-radius: 10px;
}

.step-regras__empty .empty-icon {
  color: #d0d5dd;
  margin-bottom: 8px;
}

.step-regras__empty p {
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 4px;
}

.step-regras__empty small {
  font-size: 12px;
}

.step-regras__table-wrapper {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: visible;
}

.step-regras__table {
  width: 100%;
  border-collapse: collapse;
}

.step-regras__table thead tr {
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
}

.step-regras__table thead th:first-child {
  border-top-left-radius: 10px;
}

.step-regras__table thead th:last-child {
  border-top-right-radius: 10px;
}

.step-regras__table th {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 10px 12px;
  text-align: left;
}

.step-regras__table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f2f4f7;
  vertical-align: middle;
  overflow: visible;
}

.step-regras__table tr:last-child td {
  border-bottom: none;
}

.col-coluna { width: 38%; }
.col-valor { width: 52%; }
.col-acoes { width: 10%; text-align: center; }

.step-regras__table .form-control {
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
  padding: 7px 10px;
  min-height: 36px;
  height: 36px;
  line-height: 1.4;
}

.step-regras__table .form-control:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.step-regras__table .form-control:disabled {
  background: #f9fafb;
  color: #98a2b3;
}

.multiselect-option-label {
  display: inline-block;
}

.multiselect-option-id {
  display: inline-block;
  margin-right: 8px;
  padding: 2px 6px;
  border-radius: 4px;
  background: #f2f2f8;
  color: #3a3e81;
  font-size: 11px;
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}
</style>

<style>
.step-regras__table .multiselect {
  min-height: 36px;
  font-size: 13px;
}

.step-regras__table .multiselect__tags {
  min-height: 36px;
  padding: 6px 36px 0 10px;
  border: 1px solid #dddce9;
  border-radius: 6px;
  font-size: 13px;
}

.step-regras__table .multiselect__single,
.step-regras__table .multiselect__placeholder {
  font-size: 13px;
  margin-bottom: 6px;
  padding-top: 1px;
  color: #344054;
}

.step-regras__table .multiselect__placeholder {
  color: #98a2b3;
}

.step-regras__table .multiselect--active .multiselect__tags {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.step-regras__table .multiselect__option--highlight {
  background: #3a3e81;
  color: #fff;
}

.step-regras__table .multiselect__option--highlight::after {
  background: #3a3e81;
}

.step-regras__table .multiselect__option--selected.multiselect__option--highlight {
  background: #2298a3;
}

.step-regras__table .multiselect__content-wrapper {
  border: 1px solid #dddce9;
  border-radius: 0 0 6px 6px;
  z-index: 5;
}

/* Alinha o DropdownInput (autocomplete de produto) com o multiselect */
.step-regras__table .col-valor .regra-produto {
  margin-right: 0 !important;
}

.step-regras__table .col-valor .regra-produto > label {
  display: none !important;
}

.step-regras__table .col-valor .regra-produto .form-control {
  min-height: 36px !important;
  height: 36px !important;
  padding: 7px 10px !important;
  font-size: 13px !important;
  line-height: 1.4 !important;
  border: 1px solid #dddce9 !important;
  border-radius: 6px !important;
  box-sizing: border-box !important;
}

.step-regras__table .col-valor .regra-produto .form-control:focus {
  border-color: #3a3e81 !important;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1) !important;
}
</style>
